import { useEffect, useState } from 'react'
import { ArrowLeftRight } from 'lucide-react'
import { queryKeys } from '@/lib/query-client'
import type { AdminPaymentSummary } from '@/types/api'
import { useAdminMutation } from '@/hooks/useAdminMutation'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Field } from '@/components/ui/Field'
import { Textarea } from '@/components/ui/Input'
import { KeyValue, KeyValueGrid } from '@/components/ui/RowActions'
import { changePaymentMethod } from './api'
import { formatMoney } from './describe'
import styles from './SwitchToInstapayDialog.module.css'

interface SwitchToInstapayDialogProps {
  /** The booking's id. A trip must pass `trip.requestId`. */
  tripRequestId: number | null
  bookingReference: string | null
  payment: AdminPaymentSummary | null
  onClose: () => void
  /**
   * Called after the change lands, in addition to `onClose`.
   *
   * The host is rendering a payment summary it captured when the row was
   * clicked, and that summary is now wrong in the one way that matters — the
   * method. Rather than leave a stale card booking on screen, the host closes
   * itself and the operator lands on the refreshed row.
   */
  onSwitched?: () => void
}

/**
 * Switching a stuck card booking over to Instapay.
 *
 * The call an operator is on when they open this: the customer's card was
 * declined or they abandoned the payment page, the booking is sitting in the
 * queue unpayable, and they are offering to transfer the money instead. Until
 * this existed the only answer was to cancel and re-book, which loses the
 * reference, the promo and any points already spent.
 *
 * Deliberately a dialog rather than a menu item that just does it. The change is
 * not reversible from the dashboard — there is no switch back, because handing a
 * booking to a gateway that has no order for it would leave nothing to pay — so
 * it is worth one confirmation with the amount spelled out.
 *
 * The dialog is careful about one thing above all: it must not read as "mark
 * this as paid". The booking stays unpaid, and the copy says so twice.
 */
export function SwitchToInstapayDialog({
  tripRequestId,
  bookingReference,
  payment,
  onClose,
  onSwitched,
}: SwitchToInstapayDialogProps) {
  const open = tripRequestId !== null && payment !== null
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (open) setReason('')
  }, [open, tripRequestId])

  const mutation = useAdminMutation({
    mutationFn: () =>
      changePaymentMethod(tripRequestId!, {
        method: 'INSTAPAY',
        reason: reason.trim() || undefined,
      }),
    successTitle: 'Switched to Instapay',
    successDescription: () =>
      `Booking ${bookingReference ?? ''} is now an Instapay booking. It stays unpaid until the transfer is verified.`.trim(),
    errorTitle: 'The payment method was not changed',
    // The method drives the verification badge, the row actions and whether a
    // refund would go back through the gateway or as points, so every list that
    // renders any of those is now stale.
    invalidate: [
      queryKeys.payments.all,
      queryKeys.tripRequests.all,
      queryKeys.trips.all,
      queryKeys.refunds.all,
      queryKeys.changeLog.all,
    ],
    onSuccess: () => {
      onClose()
      onSwitched?.()
    },
  })

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Switch this booking to Instapay?"
      size="md"
      busy={mutation.isPending}
      description="For a customer whose card payment did not go through and who will transfer the money instead."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            iconLeft={<ArrowLeftRight aria-hidden="true" />}
            loading={mutation.isPending}
            onClick={() => mutation.mutate(undefined)}
          >
            {mutation.isPending ? 'Switching…' : 'Switch to Instapay'}
          </Button>
        </>
      }
    >
      {payment && tripRequestId ? (
        <div className={styles.body}>
          <KeyValueGrid>
            <KeyValue label="Booking">{bookingReference ?? `#${tripRequestId}`}</KeyValue>
            <KeyValue label="Method">
              <span className={styles.transition}>
                Credit card <ArrowLeftRight size={14} aria-hidden="true" /> Instapay
              </span>
            </KeyValue>
            <KeyValue label="Amount to transfer">
              <strong className={styles.amount}>
                {formatMoney(payment.externalAmount, payment.currency)}
              </strong>
            </KeyValue>
            {payment.pointsUsedMinor > 0 ? (
              <KeyValue label="Already covered by points">
                {formatMoney(payment.pointsUsed, payment.currency)}
              </KeyValue>
            ) : null}
          </KeyValueGrid>

          {/*
            The two things an operator has to get right on this call, in the
            order they happen. Written out rather than left implied, because the
            failure this prevents is a booking switched over and then forgotten
            in the queue with nobody chasing the transfer.
          */}
          <section className={styles.next} aria-label="What happens next">
            <h3 className={styles.sectionTitle}>What happens next</h3>
            <ol className={styles.steps}>
              <li>
                Give the customer the Instapay account details from the{' '}
                <strong>Instapay account</strong> page and the amount above.
              </li>
              <li>
                When their receipt arrives, attach it here as the proof of payment. A driver cannot be
                assigned until it is on file.
              </li>
              <li>Verify the payment once the transfer shows on the account. Only that marks it paid.</li>
            </ol>
          </section>

          <p className={styles.warning}>
            This does not mark the booking as paid and moves no money. It changes how the customer is
            expected to pay.
          </p>

          <Field
            label="Reason (optional)"
            hint="Recorded in the booking's history with your name and the time."
          >
            {({ id, describedBy }) => (
              <Textarea
                id={id}
                aria-describedby={describedBy}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                rows={2}
                maxLength={500}
                placeholder="Card declined twice — customer will transfer instead"
                disabled={mutation.isPending}
              />
            )}
          </Field>
        </div>
      ) : null}
    </Dialog>
  )
}
